import React, { useState, useEffect, useRef } from "react";
import "./RechargeHistory.css";
import Sidebar from "../../../../Navigation/Sidebar/Sidebar";
import leftArrow from "../../../../../assests/leftArrow.svg";
import search from "../../../../../assests/search.svg";
import calendarIcon from "../../../../../assests/calander.svg";
import LoadingSpinner from "../../../../Loading/component";
import { useNavigate } from "react-router-dom";

const RechargeHistoryComponent = (props) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState(null);
  const [localDateFilter, setLocalDateFilter] = useState({
    startDate: props.dateFilter?.startDate || '',
    endDate: props.dateFilter?.endDate || ''
  });
  
  const dateFilterRef = useRef(null);
  const filterButtonRef = useRef(null);

  useEffect(() => {
    // Close filter when clicking outside
    const handleClickOutside = (event) => {
      if (activeFilter === 'date' && 
          dateFilterRef.current && 
          !dateFilterRef.current.contains(event.target) &&
          filterButtonRef.current && 
          !filterButtonRef.current.contains(event.target)) {
        setActiveFilter(null);
        props.setShowDateFilter(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeFilter, props]);

  const handleBackClick = () => navigate(-1);

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return "₹0.00";
    const numAmount = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]+/g, "")) : amount;
    return `₹${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Extract date directly from string without any timezone conversion
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: "", time: "", dateOnly: null };
    
    try {
      // Split the string by 'T' to separate date and time
      // Example: "2026-03-22T18:30:02" -> ["2026-03-22", "18:30:02"]
      const [datePart, timePart] = dateTimeString.split('T');
      
      if (datePart && timePart) {
        // Format date: 22 Mar 2026
        const [year, month, day] = datePart.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formattedDate = `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
        
        // Format time: 06:30 PM
        let [hours, minutes] = timePart.split(':');
        const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
        const hour12 = parseInt(hours) % 12 || 12;
        const formattedTime = `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        
        return {
          date: formattedDate,
          time: formattedTime,
          dateOnly: datePart // This is the key for filtering - YYYY-MM-DD
        };
      }
    } catch (e) {
      console.error("Date parsing error:", e);
    }
    
    return { date: "N/A", time: "", dateOnly: null };
  };

  const toggleFilter = (filterType) => {
    if (filterType === 'date') {
      if (activeFilter === 'date') {
        setActiveFilter(null);
        props.setShowDateFilter(false);
      } else {
        setActiveFilter('date');
        props.setShowDateFilter(true);
        props.setShowDepartmentFilter(false);
      }
    } else if (filterType === 'department') {
      if (activeFilter === 'department') {
        setActiveFilter(null);
        props.setShowDepartmentFilter(false);
      } else {
        setActiveFilter('department');
        props.setShowDepartmentFilter(true);
        props.setShowDateFilter(false);
      }
    }
  };

  const handleDateFilterApply = () => {
    // Apply filter if both start and end dates are selected
    if (localDateFilter.startDate && localDateFilter.endDate) {
      // Apply the filter to parent component
      props.handleDateFilterChange('startDate', localDateFilter.startDate);
      props.handleDateFilterChange('endDate', localDateFilter.endDate);
      
      // Close the filter panel
      setActiveFilter(null);
      props.setShowDateFilter(false);
      
      // Trigger filter application
      if (props.onDateFilterApply) {
        props.onDateFilterApply();
      }
    } else if (localDateFilter.startDate && !localDateFilter.endDate) {
      // Single date filter (only start date selected)
      props.handleDateFilterChange('startDate', localDateFilter.startDate);
      props.handleDateFilterChange('endDate', '');
      setActiveFilter(null);
      props.setShowDateFilter(false);
      if (props.onDateFilterApply) {
        props.onDateFilterApply();
      }
    } else if (!localDateFilter.startDate && localDateFilter.endDate) {
      // Single date filter (only end date selected)
      props.handleDateFilterChange('startDate', '');
      props.handleDateFilterChange('endDate', localDateFilter.endDate);
      setActiveFilter(null);
      props.setShowDateFilter(false);
      if (props.onDateFilterApply) {
        props.onDateFilterApply();
      }
    }
  };

  const handleDateChange = (type, value) => {
    setLocalDateFilter(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleClearDateFilter = () => {
    setLocalDateFilter({ startDate: '', endDate: '' });
    props.handleDateFilterChange('startDate', '');
    props.handleDateFilterChange('endDate', '');
    setActiveFilter(null);
    props.setShowDateFilter(false);
  };

  // Get filtered data based on date range from props
  const getFilteredData = () => {
    let filtered = props.rechargeHistory;
    
    // Apply date filter if dates are present in props
    if (props.dateFilter?.startDate && props.dateFilter?.endDate) {
      // Both dates selected - range filter
      filtered = filtered.filter(item => {
        const { dateOnly } = formatDateTime(item.rechargeTime);
        if (!dateOnly) return false;
        
        // Inclusive date comparison using string comparison
        return dateOnly >= props.dateFilter.startDate && dateOnly <= props.dateFilter.endDate;
      });
    } else if (props.dateFilter?.startDate && !props.dateFilter?.endDate) {
      // Only start date selected - filter exact date
      filtered = filtered.filter(item => {
        const { dateOnly } = formatDateTime(item.rechargeTime);
        if (!dateOnly) return false;
        
        // Exact match for single date
        return dateOnly === props.dateFilter.startDate;
      });
    } else if (!props.dateFilter?.startDate && props.dateFilter?.endDate) {
      // Only end date selected - filter exact date
      filtered = filtered.filter(item => {
        const { dateOnly } = formatDateTime(item.rechargeTime);
        if (!dateOnly) return false;
        
        // Exact match for single date
        return dateOnly === props.dateFilter.endDate;
      });
    }
    
    return filtered;
  };

  const filteredData = getFilteredData();
  const filteredCount = filteredData.length;

  return (
    <div className="rh-container">
      <Sidebar />
      {props.loading && props.rechargeHistory.length === 0 ? (
        <div className="rh-loading-fullscreen">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rh-content">
          {/* Header Section */}
          <div className="rh-header">
            <div className="rh-header-top">
              <button onClick={handleBackClick} className="rh-back-button">
                <img src={leftArrow} alt="Back" />
                <span>Back</span>
              </button>
              <div className="rh-header-info">
                <h1 className="rh-header-title">Recharge History</h1>
                <p className="rh-subtitle">
                  Track and manage all recharge transactions for Corporate ID: 
                  <span className="rh-corporate-id-badge">{props.corporateId}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className="rh-filter-controls-bar">
            <div className="rh-search-section">
              <div className="rh-search-input-wrapper">
                <img src={search} alt="Search" className="rh-search-icon" />
                <input
                  type="search"
                  placeholder="Search by Rule ID, Department or Amount..."
                  value={props.searchValue}
                  onChange={props.handleSearchInputChange}
                  onKeyPress={props.handleKeyPress}
                  className="rh-search-input"
                />
              </div>
            </div>
            
            <div className="rh-filter-buttons-section">
              <button 
                ref={filterButtonRef}
                className={`rh-filter-toggle-btn ${activeFilter === 'date' ? 'rh-filter-toggle-btn-active' : ''}`}
                onClick={() => toggleFilter('date')}
              >
                <img src={calendarIcon} alt="Date Filter" />
                Date Range
                {(props.dateFilter?.startDate || props.dateFilter?.endDate) && (
                  <span className="rh-filter-indicator"></span>
                )}
              </button>
              
              {(props.dateFilter?.startDate || props.dateFilter?.endDate || props.selectedDepartment !== "all" || props.searchValue) && (
                <button 
                  className="rh-action-btn rh-action-btn-danger"
                  onClick={props.handleClearFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Popup Date Filter */}
          {props.showDateFilter && (
            <div ref={dateFilterRef} className="rh-filter-popup">
              <div className="rh-filter-popup-content">
                <div className="rh-filter-popup-header">
                  <h4>Select Date Range</h4>
                  <button 
                    className="rh-close-filter-btn"
                    onClick={() => {
                      setActiveFilter(null);
                      props.setShowDateFilter(false);
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div className="rh-date-inputs">
                  <div className="rh-date-input-group">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={localDateFilter.startDate}
                      onChange={(e) => handleDateChange('startDate', e.target.value)}
                      max={localDateFilter.endDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="rh-date-input-group">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={localDateFilter.endDate}
                      onChange={(e) => handleDateChange('endDate', e.target.value)}
                      min={localDateFilter.startDate}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
                <div className="rh-filter-popup-actions">
                  <button 
                    className="rh-clear-date-btn"
                    onClick={handleClearDateFilter}
                    disabled={!localDateFilter.startDate && !localDateFilter.endDate}
                  >
                    Clear Dates
                  </button>
                  <button 
                    className="rh-apply-filter-btn"
                    onClick={handleDateFilterApply}
                    disabled={!localDateFilter.startDate && !localDateFilter.endDate}
                  >
                    Apply Filter
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Department Filter Panel */}
          {props.showDepartmentFilter && (
            <div className="rh-filter-panel">
              <div className="rh-filter-panel-header">
                <h4>Select Department</h4>
                <button 
                  className="rh-close-filter-btn"
                  onClick={() => toggleFilter('department')}
                >
                  ✕
                </button>
              </div>
              <div className="rh-department-options">
                {props.departments?.map(dept => (
                  <button
                    key={dept.id}
                    className={`rh-department-option ${props.selectedDepartment === dept.id ? 'rh-department-option-selected' : ''}`}
                    onClick={() => {
                      props.handleDepartmentFilterChange(dept.id);
                      toggleFilter('department');
                    }}
                  >
                    {dept.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Table Section */}
          <div className="rh-table-section">
            <div className="rh-table-header">
              <h3>Recharge Transactions</h3>
              <div className="rh-table-actions">
                <span className="rh-entries-info">
                  Showing {filteredCount} of {props.totalEntries || 0} entries
                </span>
              </div>
            </div>
            
            <div className="rh-recharge-table-container" ref={props.tableContainerRef}>
              <div className="rh-table-scroll-wrapper">
                <table className="rh-recharge-table">
                  <thead>
                    <tr>
                      <th className="rh-date-col">DATE & TIME</th>
                      <th className="rh-rule-col">RULE ID</th>
                      <th className="rh-dept-col">DEPARTMENT</th>
                      <th className="rh-amount-col">RECHARGE AMOUNT</th>
                      <th className="rh-users-col">TOTAL USERS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCount > 0 ? (
                      filteredData.map((item, index) => {
                        const { date, time } = formatDateTime(item.rechargeTime);
                        const amount = item.totalRechargeAmount || 0;
                        
                        return (
                          <tr key={`${item.ruleId}-${item.rechargeTime}-${index}`} className="rh-recharge-row">
                            <td className="rh-datetime-cell">
                              <div className="rh-datetime-content">
                                <span className="rh-date-text">{date}</span>
                                <span className="rh-time-text">{time}</span>
                              </div>
                            </td>
                            <td className="rh-rule-id-cell">
                              <span className="rh-rule-id-badge">
                                #{item.ruleId || "N/A"}
                              </span>
                            </td>
                            <td className="rh-department-cell">
                              <div className="rh-department-info">
                                <span className="rh-department-name">
                                  {item.departmentName || "N/A"}
                                </span>
                              </div>
                            </td>
                            <td className="rh-amount-cell">
                              <div className="rh-amount-display">
                                <span className="rh-amount-value">
                                  {formatAmount(amount)}
                                </span>
                              </div>
                            </td>
                            <td className="rh-users-cell">
                              <div className="rh-users-display">
                                <span className="rh-users-count">
                                  {item.totalUsers || 0}
                                </span>
                                <span className="rh-users-label">Users</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="rh-no-results">
                          <div className="rh-no-results-content">
                            <div className="rh-no-results-icon">📋</div>
                            <h4>No recharge history found</h4>
                            <p>Try adjusting your filters or search terms</p>
                            {(props.dateFilter?.startDate || props.dateFilter?.endDate || props.selectedDepartment !== "all" || props.searchValue) && (
                              <button 
                                className="rh-clear-filters-btn"
                                onClick={props.handleClearFilters}
                              >
                                Clear All Filters
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Load More Button */}
              {props.hasMore && filteredCount > 0 && (
                <div className="rh-load-more-container">
                  <button 
                    className="rh-load-more-button" 
                    onClick={props.handleLoadMore}
                    disabled={props.loadingMore}
                  >
                    {props.loadingMore ? (
                      <>
                        <span className="rh-loading-spinner-small"></span>
                        Loading More...
                      </>
                    ) : (
                      <>
                        <span>↓</span>
                        Load More Transactions
                      </>
                    )}
                  </button>
                  <p className="rh-load-more-info">
                    Showing {filteredCount} of {props.totalEntries} entries
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RechargeHistoryComponent;